import * as React from "react";
import Validator from "@/services/validatorjs";
import { iValidationErrors, iValidationRules } from "@/interfaces/validation";

interface iOptions<iFormData = Record<string, any>> {
  /**
   * Values to set for any fields when the hook mounts.
   */
  initialValues?: Partial<iFormData>;

  /**
   * Validation rules.
   * @see https://github.com/skaterdav85/validatorjs#available-rules
   */
  rules?: iValidationRules;

  /**
   * Customise the field names used when building validation messages.
   */
  validationFieldNames?: Record<string, string>;
}

type iChangeHandlerInput = null | React.ChangeEvent<any> | any;

interface iHookResult<iFormData> {
  validateForm: () => void;
  /**
   * Retrieves the value of a single field.
   *
   * @param {keyof iFormData} key
   * @returns {data['key']}
   */
  getField: (key: keyof iFormData) => iFormData[keyof iFormData];

  /**
   * Whether the data passes validation rules.
   *
   * @var {boolean}
   */
  isValid: boolean;

  /**
   * Creates a change handler that sets the value of a single field.
   *
   * @param {keyof iFormData} key
   * @returns {(input: iChangeHandlerInput) => void}
   */
  setField: (key: keyof iFormData) => (input: iChangeHandlerInput) => void;

  /**
   * Updates the value of multiple fields.
   *
   * @param {Record<keyof iFormData, iChangeHandlerInput>} map
   * @returns {void}
   */
  updateFields: (
    map: iFormData | Record<keyof iFormData, iChangeHandlerInput>,
  ) => void;

  /**
   * Helpers to access validation error results.
   *
   * @var {object}
   */
  validationErrors: {
    get: (key: keyof iFormData) => null | string[];
    all: () => iValidationErrors;
  };

  /**
   * The hook data.
   *
   * @var {iFormData}
   */
  data: iFormData;
}

const defaultOptions: iOptions = {
  rules: {},
  initialValues: {},
  validationFieldNames: {},
};

/**
 * React hook for managing form values and validation.
 *
 * @param {iOptions<iFormData>} customOptions
 * @returns {iHookResult<iFormData>}
 */
export function useForm<iFormData = Record<string, any>>(
  customOptions: iOptions<iFormData> = {},
): iHookResult<iFormData> {
  // Merge options with defaults.
  const options = {
    ...defaultOptions,
    ...customOptions,
  };

  // State that holds the current form data.
  const [data, setData] = React.useState<iFormData>(
    options.initialValues as any,
  );

  // validatejs instance. Recreated whenever the data or rules are changed.
  const validator = React.useMemo(
    () => new Validator(data, options.rules),
    [data, options.rules],
  );
  validator.setAttributeNames(options.validationFieldNames);

  /**
   * Extracts the value from a change event.
   * This allows a change handler to accept a change event, or any other value.
   *
   * @param {iChangeHandlerInput} input
   * @returns {any}
   */
  const extractValue = (input: iChangeHandlerInput): any =>
    input && input.target ? input.target.value : input;

  /**
   * Retrieves the value of a single field.
   *
   * @param {keyof iFormData} key
   * @returns {data['key']}
   */
  const getField: iHookResult<iFormData>["getField"] = (key: keyof iFormData) =>
    data[key];

  /**
   * Whether the data passes validation rules.
   *
   * @var {boolean}
   */
  const isValid: iHookResult<iFormData>["isValid"] = validator.passes();

  /**
   * Creates a change handler that sets the value of a single field.
   *
   * @param {keyof iFormData} key
   * @returns {(input: iChangeHandlerInput) => void}
   */
  const setField: iHookResult<iFormData>["setField"] =
    (key: keyof iFormData) => (input: iChangeHandlerInput) => {
      setData({
        ...data,
        [key]: extractValue(input),
      });
    };

  /**
   * Updates the value of multiple fields.
   *
   * @param {Record<keyof iFormData, iChangeHandlerInput>} map
   * @returns {void}
   */
  const updateFields: iHookResult<iFormData>["updateFields"] = (
    map: iFormData | Record<keyof iFormData, iChangeHandlerInput>,
  ) => {
    const updatedvalues = {};

    // Loop over each map item and extract the value to "updatedvalues"
    Object.keys(map).map((key) => {
      updatedvalues[key] = extractValue(map[key]);
    });

    setData({
      ...data,
      ...updatedvalues,
    });
  };

  const validationErrors = {
    get: validator.errors.get,
    all: () => {
      // transform the validatorjs object to match the interface we're using elsewhere in the app.
      const errors = validator.errors.all();
      const transformedErrors: iValidationErrors = {};
      Object.keys(errors).map((key) => {
        transformedErrors[key] = { messages: errors[key] };
      });
      return transformedErrors;
    },
  };

  /**
   * Validates the form with the current data.
   *
   * @returns {void}
   */
  const validateForm = (): void => {
    validator.validate(); // Re-run validation with the updated data
  };

  return {
    getField,
    isValid,
    setField,
    updateFields,
    validationErrors,
    data,
    validateForm,
  };
}
